import React, { useState, useCallback, useMemo, useEffect } from "react";
import Cookies from "js-cookie";
import { HighlightContext } from "../context/HighlightContext";
import { HIGHLIGHT_COOKIE_KEY } from "../utils/constants";

type PersistedHighlight = {
  ids: string[];
  color: string;
  showOnly: boolean;
};

function loadFromCookie(): PersistedHighlight | null {
  try {
    const str = Cookies.get(HIGHLIGHT_COOKIE_KEY);
    if (!str) return null;
    const raw = JSON.parse(str);
    if (!raw || typeof raw !== "object") return null;
    return {
      ids: Array.isArray(raw.ids)
        ? (raw.ids as unknown[]).filter(
            (x): x is string => typeof x === "string",
          )
        : [],
      color: typeof raw.color === "string" ? raw.color : "#FFD700",
      showOnly: raw.showOnly === true,
    };
  } catch {
    return null;
  }
}

export function HighlightProvider({ children }: { children: React.ReactNode }) {
  const initial = (() => {
    const saved = loadFromCookie();
    return {
      ids: saved ? new Set<string>(saved.ids) : new Set<string>(),
      color: saved ? saved.color : "#FFD700",
      showOnly: saved ? saved.showOnly : false,
    };
  })();

  const [isHighlightMode, setIsHighlightMode] = useState(false);
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(
    initial.ids,
  );
  const [highlightColor, setHighlightColor] = useState(initial.color);
  const [showOnlyHighlighted, setShowOnlyHighlighted] = useState(
    initial.showOnly,
  );

  const toggleHighlightMode = useCallback(() => {
    setIsHighlightMode((v) => !v);
  }, []);

  const toggleHighlight = useCallback((id: string) => {
    setHighlightedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearHighlights = useCallback(() => {
    setHighlightedIds(new Set());
    setShowOnlyHighlighted(false);
  }, []);

  const importHighlights = useCallback(
    (ids: string[], color: string, showOnly: boolean) => {
      setHighlightedIds(new Set(ids));
      setHighlightColor(color);
      setShowOnlyHighlighted(showOnly);
    },
    [],
  );

  const toggleShowOnlyHighlighted = useCallback(() => {
    setShowOnlyHighlighted((v) => !v);
  }, []);

  useEffect(() => {
    try {
      const data: PersistedHighlight = {
        ids: Array.from(highlightedIds),
        color: highlightColor,
        showOnly: showOnlyHighlighted,
      };
      Cookies.set(HIGHLIGHT_COOKIE_KEY, JSON.stringify(data), {
        expires: 365,
        sameSite: "Lax",
        path: "/",
      });
    } catch {
      // ignore
    }
  }, [highlightedIds, highlightColor, showOnlyHighlighted]);

  const value = useMemo(
    () => ({
      isHighlightMode,
      toggleHighlightMode,
      highlightedIds,
      highlightColor,
      setHighlightColor,
      toggleHighlight,
      clearHighlights,
      showOnlyHighlighted,
      toggleShowOnlyHighlighted,
      importHighlights,
    }),
    [
      isHighlightMode,
      toggleHighlightMode,
      highlightedIds,
      highlightColor,
      toggleHighlight,
      clearHighlights,
      showOnlyHighlighted,
      toggleShowOnlyHighlighted,
      importHighlights,
    ],
  );

  return (
    <HighlightContext.Provider value={value}>
      {children}
    </HighlightContext.Provider>
  );
}
